/**
 * Image Operations Module
 *
 * This module contains utilities for image-related operations including:
 * - Updating images by file path or URL
 * - Image alignment and positioning
 * - Image reference position calculations
 * - Image scaling and dimension management
 */

import { TEAM_GRAPHICS } from "constants/phase";
import { CUSTOM_IMAGE_OPTIONS, IMAGE_OPTIONS, LOGO_OPTIONS } from "constants/ThumbnailEditor";

import { findObjectByPath } from "./form-structure";
import { findParentGroupWithAlignment, layoutGroupWithAlignment } from "./group-operations";
import { updateParentGroup, waitForCanvasRender } from "./shared-utils";

/**
 * Get the reference position for an image based on its alignment
 * This is the horizontal position we want to maintain when the image's width changes
 *
 * @param {Object} img - Image object
 * @param {string} alignment - Alignment type: "center", "left", or "right"
 * @returns {number} Reference position value
 */
const getImageReferencePosition = (img, alignment) => {
  const imageWidth = img.getScaledWidth();
  const currentLeft = img.left;
  const currentOriginX = img.originX || "center";

  // Calculate the actual position of left/center/right edges
  let leftEdge, centerPos, rightEdge;

  if (currentOriginX === "center") {
    centerPos = currentLeft;
    leftEdge = currentLeft - imageWidth / 2;
    rightEdge = currentLeft + imageWidth / 2;
  } else if (currentOriginX === "left") {
    leftEdge = currentLeft;
    centerPos = currentLeft + imageWidth / 2;
    rightEdge = currentLeft + imageWidth;
  } else if (currentOriginX === "right") {
    rightEdge = currentLeft;
    centerPos = currentLeft - imageWidth / 2;
    leftEdge = currentLeft - imageWidth;
  }

  // Return the position to maintain based on desired alignment
  let reference;
  if (alignment === "center") {
    reference = centerPos;
  } else if (alignment === "left") {
    reference = leftEdge;
  } else if (alignment === "right") {
    reference = rightEdge;
  }

  return reference;
};

/**
 * Apply alignment positioning to an image
 *
 * @param {Object} img - Image object
 * @param {string} alignment - Alignment type: "center", "left", or "right"
 * @param {number} originalReference - Original reference position to maintain
 */
const applyImageAlignment = (img, alignment, originalReference) => {
  // Set origin and position based on alignment
  if (alignment === "center") {
    // Center: originX center, maintain center position
    img.set({ originX: "center", left: originalReference });
  } else if (alignment === "left") {
    // Left: originX left, maintain left edge position
    img.set({ originX: "left", left: originalReference });
  } else if (alignment === "right") {
    // Right: originX right, maintain right edge position
    img.set({ originX: "right", left: originalReference });
  }

  img.setCoords();
};

/**
 * Shared function to update image source and apply alignment
 *
 * @param {Object} editorInstance - Editor instance
 * @param {string} path - Object path
 * @param {File|string} imageSource - Image file or URL
 * @param {boolean} isFile - Whether the source is a file (true) or URL (false)
 * @param {Function} onComplete - Optional callback after image loads
 * @returns {Promise<void>}
 */
const updateImageSourceAndApplyAlignment = (editorInstance, path, imageSource, isFile, onComplete) => {
  return new Promise((resolve, reject) => {
    if (!editorInstance?.canvas || !imageSource) {
      resolve();
      return;
    }

    const obj = findObjectByPath(editorInstance.canvas, path);
    if (!obj || obj.type !== "image") {
      resolve();
      return;
    }

    // Get alignment and parent group info before loading image
    const alignment = obj?.templatePayload?.alignment;
    const parentGroupInfo = findParentGroupWithAlignment(editorInstance.canvas, path);
    const isAutoLayout = obj?.group?.templatePayload?.autoLayout;

    // Store original reference position if alignment exists (for non-group images)
    const originalReference =
      alignment && !(parentGroupInfo && isAutoLayout) ? getImageReferencePosition(obj, alignment) : null;

    if (obj?.templatePayload?.type === TEAM_GRAPHICS.LOGO) {
      _resetObjScaleMultiplier(obj);
      editorInstance.canvas.renderAll();
    }

    // Function to handle image loading
    const loadImage = (imageUrl) => {
      // Store or retrieve original placeholder dimensions
      // On first image load, store the current dimensions as the original placeholder size
      // On subsequent loads, use the stored original dimensions to prevent dimension drift
      if (!obj._originalPlaceholderWidth || !obj._originalPlaceholderHeight) {
        obj._originalPlaceholderWidth = obj.getScaledWidth();
        obj._originalPlaceholderHeight = obj.getScaledHeight();
      }

      const placeholderWidth = obj._originalPlaceholderWidth;
      const placeholderHeight = obj._originalPlaceholderHeight;

      obj.setSrc(
        imageUrl,
        async (img) => {
          try {
            // scale by height
            // const scaleY = placeholderHeight / img.height;
            // img.set({ scaleX: scaleY, scaleY: scaleY });

            // Calculate scale to fit within original placeholder dimensions
            const targetScaleX = placeholderWidth / img.width;
            const targetScaleY = placeholderHeight / img.height;
            const finalScale = Math.min(targetScaleX, targetScaleY);

            img.set({
              scaleX: finalScale,
              scaleY: finalScale,
            });

            updateParentGroup(path, editorInstance);
            editorInstance.canvas.renderAll();

            if (parentGroupInfo && isAutoLayout) {
              // Group layout takes precedence
              await waitForCanvasRender();
              layoutGroupWithAlignment(editorInstance.canvas, parentGroupInfo.path);
              await waitForCanvasRender();
            } else if (alignment && originalReference !== null) {
              // Apply image-specific alignment
              await waitForCanvasRender();
              applyImageAlignment(img, alignment, originalReference);
              editorInstance.canvas.renderAll();
            }

            // Clear any stale scale-multiplier state so the freshly loaded image
            // starts clean (base will be re-captured on the next user adjustment).
            img._scaleMultiplier = undefined;
            img._baseScaleX = undefined;
            img._baseScaleY = undefined;
            img._baseLeft = undefined;
            img._baseWidth = undefined;
            img._baseHeight = undefined;
            img._baseCenterY = undefined;

            // Call the completion callback if provided
            if (onComplete) {
              onComplete(imageUrl);
            }

            resolve();
          } catch (error) {
            console.error("❌ [updateImageSourceAndApplyAlignment] Error processing image:", error);
            reject(error);
          }
        },
        {
          crossOrigin: "anonymous",
        },
      );
    };

    // Load image based on source type
    if (isFile) {
      const reader = new FileReader();
      reader.onload = (e) => {
        loadImage(e.target.result);
      };
      reader.onerror = (error) => {
        console.error("❌ [updateImageSourceAndApplyAlignment] FileReader error:", error);
        reject(error);
      };
      reader.readAsDataURL(imageSource);
    } else {
      loadImage(imageSource);
    }
  });
};

/**
 * Updates image source of a canvas object by path (Async version with callback)
 *
 * @param {Object} editorInstance - Editor instance
 * @param {string} path - Object path
 * @param {File} imageFile - Image file to upload
 * @param {Function} onComplete - Optional callback after image loads
 * @returns {Promise<void>}
 */
export const updateImageByPathAsync = (editorInstance, path, imageFile, onComplete) => {
  return updateImageSourceAndApplyAlignment(editorInstance, path, imageFile, true, onComplete);
};

/**
 * Updates image source of a canvas object by path using a URL (Async version)
 *
 * @param {Object} editorInstance - Editor instance
 * @param {string} path - Object path
 * @param {string} imageUrl - Image URL
 * @returns {Promise<void>}
 */
export const updateImageByUrlAsync = (editorInstance, path, imageUrl) => {
  return updateImageSourceAndApplyAlignment(editorInstance, path, imageUrl, false);
};

export const getPlaceholderImage = (templatePayload) => {
  const currentElementType = templatePayload?.type;

  let placeholderImage = [...LOGO_OPTIONS, ...IMAGE_OPTIONS, ...CUSTOM_IMAGE_OPTIONS].find(
    (option) => option.value === currentElementType,
  )?.image;

  return placeholderImage;
};

export const isImageInterchangeable = (editorInstance, field) => {
  if (!["player_half_shot", "player_full_shot"].includes(field.templatePayload?.type)) {
    return { isInterchangeable: false, obj1: null, obj2: null, index1: null, index2: null };
  }

  const obj1 = findObjectByPath(editorInstance.canvas, field.path);

  const allObjects = editorInstance.canvas.getObjects();

  const obj2 = allObjects.find(
    (obj) =>
      obj.templatePayload?.type === field.templatePayload?.type &&
      obj.templatePayload?.gameGroup === field.templatePayload?.gameGroup &&
      obj.templatePayload?.key !== field.templatePayload?.key,
  );

  const index1 = allObjects?.indexOf(obj1);
  const index2 = allObjects?.indexOf(obj2);

  return { obj1, obj2, isInterchangeable: obj1 && obj2, index1, index2 };
};

// ─── Team-logo scale adjustment ──────────────────────────────────────────────

/**
 * Snapshot the current scale/position of a fabric object as its "base" so
 * that a user-controlled multiplier can always be expressed relative to a
 * stable reference even after a new image is loaded by game population.
 *
 * @param {Object} obj - Fabric image object
 */
const _saveBaseScale = (obj) => {
  obj._baseScaleX = obj.scaleX;
  obj._baseScaleY = obj.scaleY;
  obj._baseLeft = obj.left; // center X when originX = "center"
  obj._baseWidth = obj.width;
  obj._baseHeight = obj.height;
  // Store absolute center-Y so vertical anchor is preserved regardless of originY
  obj._baseCenterY = obj.top + (obj.height * obj.scaleY) / 2;
};

/**
 * Apply a scale multiplier to a fabric object using stored base values.
 * Internal – always call _saveBaseScale first if base is not yet set.
 *
 * @param {Object} obj
 * @param {number} multiplier
 */
const _applyScaleToObj = (obj, multiplier) => {
  if (obj._baseScaleX === undefined) return;

  const newScaleX = obj._baseScaleX * multiplier;
  const newScaleY = obj._baseScaleY * multiplier;

  obj.set({
    scaleX: newScaleX,
    scaleY: newScaleY,
    left: obj._baseLeft,
    top: obj._baseCenterY - (obj._baseHeight * newScaleY) / 2,
  });
};

/**
 * Apply a user-controlled scale multiplier to a team logo by canvas path.
 *
 * The multiplier is persisted directly on the fabric object so that
 * when game population reloads the image via updateImageSourceAndApplyAlignment,
 * the adjustment is automatically re-applied after the new image settles.
 *
 * @param {Object} editorInstance
 * @param {string} path       - Canvas object path
 * @param {number} multiplier - Scale factor relative to the object's base size (1 = original)
 */
export const applyLogoScaleAdjustmentByPath = (editorInstance, path, multiplier) => {
  const obj = findObjectByPath(editorInstance.canvas, path);
  if (!obj) return;

  // Capture baseline on first call (before any multiplier has been applied)
  if (obj._baseScaleX === undefined) {
    _saveBaseScale(obj);
  }

  _applyScaleToObj(obj, multiplier);

  // Persist for automatic re-application when the logo image changes
  obj._scaleMultiplier = multiplier;

  editorInstance.canvas.renderAll();
};

// ─── Reset helpers ────────────────────────────────────────────────────────────

/**
 * Recursively collect every fabric object from the canvas, including objects
 * that are nested inside groups.
 *
 * @param {Object} canvas - Fabric canvas instance
 * @returns {Object[]}
 */
const _getAllCanvasObjectsRecursively = (canvas) => {
  const results = [];
  const traverse = (objects) => {
    objects.forEach((obj) => {
      results.push(obj);
      if (obj._objects) traverse(obj._objects);
    });
  };
  traverse(canvas.getObjects());
  return results;
};

/**
 * Restore a single fabric object to its natural-fit scale (multiplier = 1)
 * and wipe all stored base-scale bookkeeping so the next image load starts
 * from a clean slate. Only applies to team-logo objects.
 *
 * @param {Object} obj - Fabric image object
 */
const _resetObjScaleMultiplier = (obj) => {
  if (obj?.templatePayload?.type !== TEAM_GRAPHICS.LOGO) return;
  if (obj._baseScaleX === undefined) return;

  _applyScaleToObj(obj, 1);
  obj._scaleMultiplier = undefined;
  obj._baseScaleX = undefined;
  obj._baseScaleY = undefined;
  obj._baseLeft = undefined;
  obj._baseWidth = undefined;
  obj._baseHeight = undefined;
  obj._baseCenterY = undefined;
};

/**
 * Reset scale-multiplier adjustments for ALL logo objects on the canvas
 * (including those nested inside groups). Intended to be called before
 * game population so every logo is at its natural-fit position before new
 * images are loaded.
 *
 * @param {Object} editorInstance
 */
export const resetAllLogoScaleAdjustments = (editorInstance) => {
  if (!editorInstance?.canvas) return;
  const allObjects = _getAllCanvasObjectsRecursively(editorInstance.canvas);
  allObjects.forEach(_resetObjScaleMultiplier);
  editorInstance.canvas.renderAll();
};

// ─────────────────────────────────────────────────────────────────────────────

export const interchangeObjectLayers = (editorInstance, field) => {
  const { obj1, obj2, index1, index2 } = isImageInterchangeable(editorInstance, field);

  if (!obj1 || !obj2) {
    console.warn("interchangeObjectLayers: Both objects must be provided");
    return;
  }

  editorInstance?.canvas?.moveTo(obj1, index2);
  editorInstance?.canvas?.moveTo(obj2, index1);
};
